// Admin -> Ops -> Alerts. The cards alert triage posted to the cxhub Teams
// channel, in the app: every alert that fired, what the investigation made
// of it, who holds the lever, and the morning trend reviews. Read-only. The
// change a card proposes is still a person's edit and a Terraform apply.
//
// Nothing here is customer content: the triage prompt forbids it and the
// script scrubs email addresses and phone numbers before a card is posted.

import { useEffect, useState } from 'react'
import {
  Box, Typography, Stack, Chip, Paper, CircularProgress, Button, Alert, Collapse, Link,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material'
import { fetchAlertEvents } from './api.js'

// One kind -> how it reads at a glance. Same words as the Teams card.
const KIND = {
  action: { color: 'error', label: 'Action needed' },
  watch: { color: 'warning', label: 'Watch' },
  noise: { color: 'success', label: 'Noise' },
  resolved: { color: 'success', label: 'Resolved' },
  clear: { color: 'success', label: 'All clear' },
  failed: { color: 'error', label: 'Triage failed' },
  expiring: { color: 'warning', label: 'Token expiring' },
  trend: { color: 'info', label: 'Trend review' },
}

const CONTROL = {
  ours: { color: 'warning', label: 'Change needed: ours' },
  alert: { color: 'warning', label: 'Change needed: alert rule' },
  upstream: { color: 'default', label: 'Upstream, not ours' },
}

function when(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function shortRule(rule) {
  return rule ? rule.replace(/^alert-cxhubv2-/, '') : ''
}

function EventRow({ ev }) {
  const [open, setOpen] = useState(false)
  const kind = KIND[ev.kind] ?? { color: 'default', label: ev.kind }
  const control = ev.control ? CONTROL[ev.control] : null
  const hasMore = ev.summary || ev.evidence?.length || ev.lever || ev.proposals?.length || ev.nextStep
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" color={kind.color} label={kind.label} />
        {control && <Chip size="small" color={control.color} variant="outlined" label={control.label} />}
        {ev.rule && <Chip size="small" variant="outlined" label={shortRule(ev.rule)} />}
        {ev.severity && <Typography variant="caption" color="text.secondary">{ev.severity}</Typography>}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{when(ev.createdAt)}</Typography>
      </Stack>
      <Typography variant="subtitle2" sx={{ mt: 0.75 }}>{ev.title}</Typography>
      {ev.lever && (
        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
          {ev.control === 'upstream' ? 'Lever' : 'Change'}: {ev.lever}
        </Typography>
      )}
      {hasMore && (
        <Button size="small" onClick={() => setOpen((o) => !o)} sx={{ mt: 0.5, px: 0 }}>{open ? 'Less' : 'More'}</Button>
      )}
      <Collapse in={open}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {ev.summary && <Typography variant="body2">{ev.summary}</Typography>}
          {ev.evidence?.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {ev.evidence.map((e, i) => <Typography key={i} component="li" variant="body2" color="text.secondary">{e}</Typography>)}
            </Box>
          )}
          {ev.proposals?.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Proposed changes</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {ev.proposals.map((p, i) => (
                  <Typography key={i} component="li" variant="body2">
                    {p.rule_or_component}{p.control ? ` (${CONTROL[p.control]?.label ?? p.control})` : ''}: {p.lever}{p.reason ? ` ${p.reason}` : ''}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {ev.nextStep && <Typography variant="body2" sx={{ fontWeight: 600 }}>Next: {ev.nextStep}</Typography>}
          <Stack direction="row" spacing={2}>
            {ev.firedAt && <Typography variant="caption" color="text.secondary">Fired {when(ev.firedAt)}</Typography>}
            {ev.resolvedAt && <Typography variant="caption" color="text.secondary">Resolved {when(ev.resolvedAt)}</Typography>}
            {ev.runUrl && <Link variant="caption" href={ev.runUrl} target="_blank" rel="noopener">Investigation log</Link>}
          </Stack>
        </Stack>
      </Collapse>
    </Paper>
  )
}

export default function AlertsView() {
  const [days, setDays] = useState(7)
  const [rule, setRule] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchAlertEvents({ days, rule: rule || null }))
    } catch (err) {
      setError(err?.message || 'Could not load the alert log')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [days, rule]) // eslint-disable-line react-hooks/exhaustive-deps

  const events = data?.events ?? []
  const rules = data?.rules ?? []
  const changes = events.filter((e) => e.control === 'ours' || e.control === 'alert' || e.proposals?.length)

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="h6">Alerts</Typography>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_e, v) => v && setDays(v)}>
          <ToggleButton value={1}>Today</ToggleButton>
          <ToggleButton value={7}>7 days</ToggleButton>
          <ToggleButton value={30}>30 days</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="alerts-rule">Rule</InputLabel>
          <Select labelId="alerts-rule" label="Rule" value={rule} onChange={(e) => setRule(e.target.value)}>
            <MenuItem value="">All rules</MenuItem>
            {rules.map((r) => <MenuItem key={r.rule} value={r.rule}>{shortRule(r.rule)} ({r.cards})</MenuItem>)}
          </Select>
        </FormControl>
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
        {loading && <CircularProgress size={18} />}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {events.length} cards, {changes.length} with a change to make
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every card alert triage posted to the cxhub Teams channel. A card marked "Change needed" names the file and the value to change;
        the change is still a person's edit and a Terraform apply. "Upstream" means TRQ, Foundry, eBay or Graph, and nothing here changes it.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!error && !loading && events.length === 0 && (
        <Alert severity="info">No cards in this window. The triage identity needs the alert_writer role before the first card can land here (docs/monitoring.md).</Alert>
      )}
      <Stack spacing={1}>
        {events.map((ev) => <EventRow key={ev.id} ev={ev} />)}
      </Stack>
    </Box>
  )
}
